import axios from 'axios';
import toast from 'react-hot-toast';
import { isSBXBetEnvironment } from './authUtils';

const getHost = (): string => {
  if (process.env.REACT_APP_ENV === 'LOCALHOST') {
    return `${process.env.REACT_APP_API_ENDPOINT}`;
  }

  return window.location.origin;
};

export const configurationFromDomain = (domain: string): string | null => {
  const ktbackMatch = domain.match(/^([a-z0-9-]+)\.(?:ktback\.net|ktback\.dev)$/i);
  if (ktbackMatch) {
    return ktbackMatch[1].toUpperCase();
  }

  const sbxMatch = domain.match(/^([a-z0-9-]+)\.(?:sbx\.bet|sbxcloud\.dev)$/i);
  if (sbxMatch) {
    const config = sbxMatch[1].toUpperCase().replace(/-CMS$/, '');
    return config === 'SB-S2B' ? 'S2B' : config;
  }

  const genericMatch = domain.match(/([a-z0-9]+)-cms[^.]*\./i);
  if (genericMatch) return genericMatch[1].toUpperCase();

  if (domain.includes('core.websbkt.com')) return 'MHS';

  return null;
};

export const isAutoConfigHostname = (hostname: string): boolean => (
  /sbx\.bet$|sbxcloud\.dev$|ktback\.dev$/i.test(hostname)
);

export const buildRequestParams = (data: Record<string, unknown>): Record<string, unknown> => {
  const configuration = localStorage.getItem('userConfiguration');
  const token = localStorage.getItem('authToken');
  return {
    configuration,
    token,
    ...data,
  };
};

interface GenericError {
  response: {
    data: {
      success?: boolean;
      error?: string;
    }
    status: number
  }
}

export const POST = (
  path: string,
  data: Record<string, unknown> | FormData,
  disableRedirect?: boolean,
  hideErrorMessage?: boolean,
): Promise<any> => {
  const domain = getHost();

  return axios.post(`${domain}/${path}`, buildRequestParams({ ...data }), {
    timeout: 180000,
  })
    .then((response) => response.data)
    .catch((error: GenericError) => {
      if (!hideErrorMessage) {
        const errorMessage = error
        && error.response
        && error.response.data
        && error.response.data.error;
        const message = errorMessage || 'Something went wrong';
        toast.error(message);
      }
      return Promise.reject(error);
    });
};

export const FETCHPOST = (
  path: string,
  data: Record<string, unknown> | FormData,
): Promise<any> => {
  const domain = getHost();

  return fetch(`${domain}/${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(buildRequestParams({ ...data })),
  })
    .catch((error: GenericError) => {
      const errorMessage = error
          && error.response
          && error.response.data
          && error.response.data.error;
      const message = errorMessage || 'Something went wrong';
      toast.error(message);

      if (error.response.status === 401) {
        window.location.href = '/';
      }
    });
};

const FORM_CONTENT_TYPE = 'application/x-www-form-urlencoded';

export const POST_ON_UNLOAD = (
  path: string,
  data: Record<string, unknown>,
): void => {
  const domain = getHost();
  const url = `${domain}/${path}`;
  const params = new URLSearchParams();

  Object.entries(buildRequestParams({ ...data })).forEach(([key, value]) => {
    if (value !== undefined && value !== null) params.append(key, String(value));
  });

  const body = params.toString();

  if (navigator.sendBeacon?.(url, new Blob([body], { type: FORM_CONTENT_TYPE }))) return;

  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': FORM_CONTENT_TYPE,
    },
    body,
    keepalive: true,
  }).catch(() => undefined);
};

export const FETCHGET = (
  path: string,
  params: Record<string, unknown>,
): Promise<any> => {
  const domain = getHost();
  const queryParams = new URLSearchParams(
    Object.entries(buildRequestParams({ ...params }))
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, String(value)]),
  );

  return fetch(`${domain}/${path}?${queryParams.toString()}`)
    .catch((error: GenericError) => {
      const errorMessage = error
          && error.response
          && error.response.data
          && error.response.data.error;
      const message = errorMessage || 'Something went wrong';
      toast.error(message);

      if (error.response.status === 401) {
        window.location.href = '/';
      }
    });
};

export const PUT = (
  path: string,
  data: Record<string, unknown>,
): Promise<any> => {
  const domain = getHost();

  return axios({
    method: 'put',
    url: `${domain}/${path}`,
    data: buildRequestParams({ ...data }),
  })
    .then((response) => response.data)
    .catch((error: GenericError) => {
      const errorMessage = error
          && error.response
          && error.response.data
          && error.response.data.error;
      const message = errorMessage || 'Something went wrong';
      toast.error(message);
      if (error.response.status === 401) {
        window.location.href = '/';
      }
      return error.response.data;
    });
};

export const GET = (
  path: string,
  params?: {
    [key: string]: string | number | undefined | null | boolean | number[] | string[];
  },
  hideErrorMessage?: boolean,
): Promise<any> => {
  const domain = getHost();
  return axios({
    method: 'get',
    url: `${domain}/${path}`,
    params: buildRequestParams({ ...params }),
  }).then((response) => response.data)
    .catch((error: GenericError) => {
      if (!hideErrorMessage) {
        const errorMessage = error
          && error.response
          && error.response.data
          && error.response.data.error;
        const message = errorMessage || 'Something went wrong';
        toast.error(message);
      }
      if (error.response.status === 401) {
        window.location.href = '/';
      }
      return error.response.data;
    });
};

export const DELETE = (
  path: string,
  params?: {
    [key: string]: string | number | boolean;
  },
  hideErrorMessage?: boolean,
): Promise<any> => {
  const domain = getHost();
  return axios({
    method: 'delete',
    url: `${domain}/${path}`,
    params: buildRequestParams({ ...params }),
  }).then((response) => response.data)
    .catch((error: GenericError) => {
      if (!hideErrorMessage) {
        const errorMessage = error
        && error.response
        && error.response.data
        && error.response.data.error;
        let message = errorMessage || 'Something went wrong';

        if (error.response.status === 409) {
          message = 'This item has related data in use and cannot be deleted';
        }

        toast.error(message);
      }
      return Promise.reject(error);
    });
};

type GenericRequestBody = { [key: string]: string| number| boolean | null }

export const addTokenToRequest = (requestData: GenericRequestBody): GenericRequestBody => {
  const configuration = localStorage.getItem('userConfiguration');
  const token = localStorage.getItem('authToken');

  return {
    ...requestData,
    configuration,
    token,
  };
};

export const isUatEnvironment = (): boolean => {
  const host = window.location.hostname.toLowerCase();
  return host.endsWith('-uat.websport.dev');
};

export const signalRendpointResolver = (configuration: string): string | undefined => {
  const isDevelopment = process.env.REACT_APP_ENV !== 'production' || isUatEnvironment();
  const isSbx = isSBXBetEnvironment(configuration);

  if (isDevelopment) {
    return `https://ws-${configuration}.websport.dev`;
  }

  if (isSbx) {
    return `https://ws-${configuration}.sbx.bet`;
  }

  return `https://ws-${configuration}.websbkt.com`;
};
